// Local Payment Service - No backend required
import localAuthService from './localAuthService';

class LocalPaymentService {
  constructor() {
    this.transactions = this.loadTransactions();
    this.paymentMethods = this.loadPaymentMethods();
    this.balances = this.loadBalances();
  }

  // Load transactions from localStorage
  loadTransactions() {
    const transactions = localStorage.getItem('silva_transactions');
    return transactions ? JSON.parse(transactions) : [
      {
        id: '1',
        userId: '2',
        type: 'payment',
        amount: 150.00,
        currency: 'USD',
        status: 'completed',
        description: 'Pago de envío #12345',
        paymentMethod: 'credit_card',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 86400000).toISOString()
      },
      {
        id: '2',
        userId: '2',
        type: 'refund',
        amount: 25.00,
        currency: 'USD',
        status: 'completed',
        description: 'Reembolso por cancelación',
        paymentMethod: 'credit_card',
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        updatedAt: new Date(Date.now() - 172800000).toISOString()
      }
    ];
  }

  // Save transactions to localStorage
  saveTransactions() {
    localStorage.setItem('silva_transactions', JSON.stringify(this.transactions));
  }

  // Load payment methods from localStorage
  loadPaymentMethods() {
    const methods = localStorage.getItem('silva_payment_methods');
    return methods ? JSON.parse(methods) : [
      {
        id: '1',
        userId: '2',
        type: 'credit_card',
        name: 'Tarjeta Principal',
        last4: '1234',
        brand: 'Visa',
        isDefault: true,
        createdAt: new Date().toISOString()
      },
      {
        id: '2',
        userId: '2',
        type: 'bank_account',
        name: 'Cuenta Bancaria',
        accountNumber: '****5678',
        bankName: 'Banco Demo',
        isDefault: false,
        createdAt: new Date().toISOString()
      }
    ];
  }

  // Save payment methods to localStorage
  savePaymentMethods() {
    localStorage.setItem('silva_payment_methods', JSON.stringify(this.paymentMethods));
  }

  // Load balances from localStorage
  loadBalances() {
    const balances = localStorage.getItem('silva_balances');
    return balances ? JSON.parse(balances) : {
      '2': {
        available: 500.00,
        pending: 75.00,
        total: 575.00,
        currency: 'USD',
        lastUpdated: new Date().toISOString()
      }
    };
  }

  // Save balances to localStorage
  saveBalances() {
    localStorage.setItem('silva_balances', JSON.stringify(this.balances));
  }

  // Get current user
  async getCurrentUser() {
    return await localAuthService.getCurrentUser();
  }

  // Get user balance
  async getBalance() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const balance = this.balances[user.id] || {
      available: 0,
      pending: 0,
      total: 0,
      currency: 'USD',
      lastUpdated: new Date().toISOString()
    };

    return balance;
  }

  // Get payment methods
  async getPaymentMethods() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    return this.paymentMethods.filter(method => method.userId === user.id);
  }

  // Add payment method
  async addPaymentMethod(methodData) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const newMethod = {
      id: Date.now().toString(),
      userId: user.id,
      ...methodData,
      createdAt: new Date().toISOString()
    };

    this.paymentMethods.push(newMethod);
    this.savePaymentMethods();

    return {
      success: true,
      method: newMethod,
      message: 'Método de pago agregado exitosamente'
    };
  }

  // Update payment method
  async updatePaymentMethod(methodId, methodData) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const methodIndex = this.paymentMethods.findIndex(
      m => m.id === methodId && m.userId === user.id
    );

    if (methodIndex === -1) {
      throw new Error('Método de pago no encontrado');
    }

    this.paymentMethods[methodIndex] = {
      ...this.paymentMethods[methodIndex],
      ...methodData,
      updatedAt: new Date().toISOString()
    };

    this.savePaymentMethods();

    return {
      success: true,
      method: this.paymentMethods[methodIndex],
      message: 'Método de pago actualizado exitosamente'
    };
  }

  // Delete payment method
  async deletePaymentMethod(methodId) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const methodIndex = this.paymentMethods.findIndex(
      m => m.id === methodId && m.userId === user.id
    );

    if (methodIndex === -1) {
      throw new Error('Método de pago no encontrado');
    }

    this.paymentMethods.splice(methodIndex, 1);
    this.savePaymentMethods();

    return {
      success: true,
      message: 'Método de pago eliminado exitosamente'
    };
  }

  // Process payment
  async processPayment(paymentData) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const transaction = {
      id: Date.now().toString(),
      userId: user.id,
      type: 'payment',
      amount: paymentData.amount,
      currency: paymentData.currency || 'USD',
      status: 'completed',
      description: paymentData.description,
      paymentMethod: paymentData.paymentMethod,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.transactions.push(transaction);
    this.saveTransactions();

    // Update balance
    if (!this.balances[user.id]) {
      this.balances[user.id] = {
        available: 0,
        pending: 0,
        total: 0,
        currency: 'USD',
        lastUpdated: new Date().toISOString()
      };
    }

    this.balances[user.id].available += paymentData.amount;
    this.balances[user.id].total += paymentData.amount;
    this.balances[user.id].lastUpdated = new Date().toISOString();
    this.saveBalances();

    return {
      success: true,
      transaction,
      message: 'Pago procesado exitosamente'
    };
  }

  // Process refund
  async processRefund(transactionId, amount) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const originalTransaction = this.transactions.find(
      t => t.id === transactionId && t.userId === user.id
    );

    if (!originalTransaction) {
      throw new Error('Transacción no encontrada');
    }

    const refundTransaction = {
      id: Date.now().toString(),
      userId: user.id,
      type: 'refund',
      amount: amount,
      currency: originalTransaction.currency,
      status: 'completed',
      description: `Reembolso de ${originalTransaction.description}`,
      paymentMethod: originalTransaction.paymentMethod,
      originalTransactionId: transactionId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.transactions.push(refundTransaction);
    this.saveTransactions();

    // Update balance
    this.balances[user.id].available -= amount;
    this.balances[user.id].total -= amount;
    this.balances[user.id].lastUpdated = new Date().toISOString();
    this.saveBalances();

    return {
      success: true,
      transaction: refundTransaction,
      message: 'Reembolso procesado exitosamente'
    };
  }

  // Get transactions
  async getTransactions(filters = {}) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    let transactions = this.transactions.filter(t => t.userId === user.id);

    // Apply filters
    if (filters.type) {
      transactions = transactions.filter(t => t.type === filters.type);
    }

    if (filters.status) {
      transactions = transactions.filter(t => t.status === filters.status);
    }

    if (filters.startDate) {
      transactions = transactions.filter(t => new Date(t.createdAt) >= new Date(filters.startDate));
    }

    if (filters.endDate) {
      transactions = transactions.filter(t => new Date(t.createdAt) <= new Date(filters.endDate));
    }

    // Sort by date (newest first)
    transactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return transactions;
  }

  // Get transaction by ID
  async getTransaction(transactionId) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const transaction = this.transactions.find(
      t => t.id === transactionId && t.userId === user.id
    );

    if (!transaction) {
      throw new Error('Transacción no encontrada');
    }

    return transaction;
  }

  // Get payment analytics
  async getPaymentAnalytics() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const userTransactions = this.transactions.filter(t => t.userId === user.id);
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const recentTransactions = userTransactions.filter(
      t => new Date(t.createdAt) >= thirtyDaysAgo
    );

    const totalPayments = recentTransactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalRefunds = recentTransactions
      .filter(t => t.type === 'refund')
      .reduce((sum, t) => sum + t.amount, 0);

    const paymentMethods = recentTransactions.reduce((acc, t) => {
      acc[t.paymentMethod] = (acc[t.paymentMethod] || 0) + 1;
      return acc;
    }, {});

    return {
      totalPayments,
      totalRefunds,
      netAmount: totalPayments - totalRefunds,
      transactionCount: recentTransactions.length,
      paymentMethods,
      balance: await this.getBalance()
    };
  }

  // Transfer funds
  async transferFunds(transferData) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    if (!this.balances[user.id] || this.balances[user.id].available < transferData.amount) {
      throw new Error('Saldo insuficiente');
    }

    const transferTransaction = {
      id: Date.now().toString(),
      userId: user.id,
      type: 'transfer',
      amount: transferData.amount,
      currency: transferData.currency || 'USD',
      status: 'completed',
      description: `Transferencia a ${transferData.recipient}`,
      recipient: transferData.recipient,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.transactions.push(transferTransaction);
    this.saveTransactions();

    // Update balance
    this.balances[user.id].available -= transferData.amount;
    this.balances[user.id].total -= transferData.amount;
    this.balances[user.id].lastUpdated = new Date().toISOString();
    this.saveBalances();

    return {
      success: true,
      transaction: transferTransaction,
      message: 'Transferencia realizada exitosamente'
    };
  }
}

// Create singleton instance
const localPaymentService = new LocalPaymentService();

export default localPaymentService; 